import { useState, useMemo } from 'react';
import { usePlans } from '../../../hooks/usePlans';
import { useGroups } from '../../../hooks/useGroups';
import { useLegalEntities } from '../../../hooks/useLegalEntities';
import { useCouponsStatistics } from '../../../hooks/useStatistics';
import {
  INITIAL_FILTERS,
  COLLECTING_ENTITIES_REPORT_TYPES,
  MESSAGES,
} from '../constants/collectingEntitiesConstants';

export const useCollectingEntitiesStatistics = () => {
  const { data: plans = [], isLoading: plansLoading } = usePlans();
  const { data: groups = [], isLoading: groupsLoading } = useGroups();
  const { data: legalEntities = [], isLoading: legalEntitiesLoading } = useLegalEntities();
  const { mutateAsync: fetchStatistics } = useCouponsStatistics();

  const [filters, setFilters] = useState({ ...INITIAL_FILTERS });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState([]);
  const [totals, setTotals] = useState({ groups: {}, total: 0 });
  const [header, setHeader] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const catalogsLoading = plansLoading || legalEntitiesLoading || groupsLoading;

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (error) {
      setError(null);
    }
  };

  const validateFilters = () => {
    if (!filters.dateStart || !filters.dateEnd) {
      return MESSAGES.ERROR.DATES_REQUIRED;
    }
    if (filters.dateStart && filters.dateEnd && filters.dateStart > filters.dateEnd) {
      return MESSAGES.ERROR.INVALID_RANGE;
    }
    if (
      filters.reportType === COLLECTING_ENTITIES_REPORT_TYPES.INTERMEDIATE &&
      (!filters.legalEntity || filters.legalEntity === 'all')
    ) {
      return MESSAGES.ERROR.LEGAL_ENTITY_REQUIRED;
    }
    return null;
  };

  const handleSubmit = async (event) => {
    event?.preventDefault();

    const validationError = validateFilters();
    if (validationError) {
      setError(validationError);
      return;
    }

    setModalOpen(false);
    setLoading(true);
    setError(null);

    try {
      const payload = {
        ...filters,
        reportType: filters.reportType || COLLECTING_ENTITIES_REPORT_TYPES.SUMMARY,
      };

      const response = await fetchStatistics(payload);
      setHeader(response.header || null);
      setResults(response.results || []);
      setTotals(response.totals || { groups: {}, total: 0 });
      setModalOpen(true);
    } catch (err) {
      console.error('Error fetching bank statistics:', err);
      setError(MESSAGES.ERROR.FETCH_DATA);
      setHeader(null);
      setResults([]);
      setTotals({ groups: {}, total: 0 });
      setModalOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const groupColumns = useMemo(() => header?.group_columns || [], [header]);

  const handleCloseModal = () => setModalOpen(false);

  return {
    filters,
    loading,
    error,
    results,
    totals,
    header,
    plans,
    groups,
    legalEntities,
    catalogsLoading,
    groupColumns,
    modalOpen,
    handleFilterChange,
    handleSubmit,
    handleCloseModal,
  };
};
